import styled from "styled-components";
import { FiCreditCard } from "react-icons/fi";
import { colors } from "../../../utils";
import { SETTINGS_CRUNCH_FIX } from "../../../constants/featureFlags";
import { planNameNoWrapCss } from "../../../constants/settingsCrunchFix";
import { gutterX } from "../../../theme/tokens";

// settings-crunch: OFF -> "" (interpolation emits nothing, byte-identical); ON ->
// pins nowrap+ellipsis on the plan-name element so "Premium" never breaks mid-word.
const planNameNoWrap = planNameNoWrapCss(SETTINGS_CRUNCH_FIX);

export const CenterWrapper = styled.div`
  background-color: ${colors.white};
  border-radius: 10px;
  padding: 3vw ${gutterX()};
`;

export const ContentHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 10px;
`;

export const HeaderIcon = styled(FiCreditCard)`
  margin-right: 10px;
  width: 40px;
  height: 40px;
  color: ${colors.PrimaryPurple};
`;

export const BillingTitle = styled.div`
  display: flex;
  flex-direction: column;
`;

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  width: 100%;
  border-radius: 8px;
`;

export const CardContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 24px;
    flex: 1;
    min-width: 0;

    /* Mobile: two half-cards cannot fit side-by-side at 375/430; stack them so
       neither card is forced off-screen right. */
    @media (max-width: 600px) {
        flex-direction: column;
        align-items: stretch;
    }
`

export const SingleCard = styled.div`
    display: flex;
    gap: 8px;
    flex-direction: column;
    flex: 0.5;
    /* Flex items default to min-width:auto and refuse to shrink below their
       widest fixed-width child (SecurePaymentText/AddPaymentButton). Allow the
       card to shrink to the available column width. */
    min-width: 0;
`

export const CardSubContainer = styled.div`
    display: flex;
    justify-content: space-between;
`

export const CardHeading = styled.div`
    font-size: ${props => (props.fontSize || "16px")};
    line-height: 20px;
    font-weight: ${props => (props.fontWeight || 700)};
    font-family: "DM Sans", sans-serif;
    color: #123051;
`

export const InternalHeading = styled.div`
    font-size: 20px;
    line-height: 20px;
    font-weight: 700;
    font-family: "DM Sans", sans-serif;
    color: #123051;
`

export const PlanInfoContainer = styled.div`
    display: flex;
    border-radius: 8px;
    padding: 24px 40px 24px 40px;
    gap: 24px;
    border: 1px solid #E1E1E1;
    justify-content: space-between;
    height: ${props => (props.hasCard ? "134px" : "176px")};
    min-width: 0;

    /* Mobile: trim the 40px side padding so plan/price content fits within a
       narrow (375px) viewport. */
    @media (max-width: 600px) {
        padding: 20px 16px;
        gap: 12px;
    }
`

export const PlanHeadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 1px;
`

export const NoCardContainer = styled.div`
    border: 1px solid #E1E1E1;
    padding: 24px;
    border-radius: 8px;
    justify-content: center;
    align-items: center;
`

export const SubContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 16px;
`

export const Duration = styled.div`
    border-radius: 100px;
    background-color: #E3EFEB;
    padding: 4px 8px 4px 8px;
    height: fit-content;
`

// export const Card = styled.div`
//     display: flex;
//     justify-content: space-between;
//     border-radius: 8px;
//     border: 1px solid #CED4DA;
//     padding: 24px;
//     background: conic-gradient(from 180deg at 50% 50%, #F5EEDE -41.73deg, #F7FED5 53.81deg, #CCFEFF 133.87deg, #FDCFFE 244.39deg, #F5EEDE 318.27deg, #F7FED5 413.81deg);
// `

export const CardInfo = styled.div`
    display: flex;
    gap: 10px;
    align-items: center;
`

export const EditButton = styled.div`
    color: #9B5AF8;
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;
    font-family: "DM Sans", sans-serif;
    cursor: pointer;
    &:focus-visible {
        outline: 2px solid #625B71;
        outline-offset: 2px;
    }
`

export const TableContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-top: 20px;
    min-width: 0;

    /* Mobile: the shared <Table> forces every column to a fixed 120px via
       emotion (.css-HASH .rdt_TableCol, specificity 0,2,0), pushing the last
       "Charge" column off-screen right at 375/430. v20 added a plain-specificity
       override here (.sc .rdt_TableCol, also 0,2,0) which TIED emotion and lost
       the source-order tie-break, so columns stayed 120px and the ~528px table
       only scrolled inside its overflow box (never actually shrank).
       Fix: double this scoped container class (&&  ->  .sc.sc, specificity 0,3,0)
       so the billing-only override decisively beats emotion. Columns then flex
       (flex:1 0 0) to share the viewport and fit. Scoped to THIS billing history
       table only — the shared Table global and every other table are untouched;
       desktop (>600px) is outside the query and unchanged.
       MEASURED on live web-stg (QAFIX v21-billing-table): table 528->339px @375
       and ->390px @430, scroll box no longer overflows, page keeps no h-scroll. */
    @media (max-width: 600px) {
        && .rdt_TableCol,
        && .rdt_TableCell {
            width: auto;
            min-width: 0;
            padding: 4px 6px;
        }
        && .rdt_Table {
            min-width: 0;
        }
        /* Drop the 12px head-row letter-spacing + one point of font size so the
           single (unbreakable) word "Transaction" fits its narrowed column
           instead of clipping; multi-word headers (e.g. "Payment Method") wrap. */
        && .rdt_TableHeadRow {
            letter-spacing: normal;
            font-size: 11px;
        }
        && .rdt_TableCol > div {
            white-space: normal;
            overflow: visible;
            text-overflow: clip;
        }
    }
`

export const TableHeadingContainer = styled.div`
    display: flex;
    justify-content: space-between;

    /* Mobile: the title + filter/action buttons cannot sit on one row at
       375/430 without overflowing. Stack them. */
    @media (max-width: 600px) {
        flex-direction: column;
        align-items: stretch;
        gap: 12px;
    }
`

export const BillingPrice = styled.div`
    font-size: 32px;
    font-weight: 700;
    line-height: 41.66px;
    font-family: "DM Sans", sans-serif;
    color: #565556;
`

export const CardHeadingContainer = styled.div`
    display: flex;
    justify-content: space-between;
`

export const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
`

export const AddPaymentButton = styled.div`
    display: flex;
    align-self: center;
    width: 190px;
    max-width: 100%;
`

export const FilterContainer = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;

    /* Mobile: allow the download/export/filter controls to wrap instead of
       forcing the row wider than the viewport. */
    @media (max-width: 600px) {
        flex-wrap: wrap;
    }
`

export const PlanName = styled.div`
  display: flex;
  gap: 8px;
  align-items: ${props => (props.hasDueDate ? 'center' : 'normal')};
  ${planNameNoWrap && `& > div:first-child { ${planNameNoWrap} }`}
`;

export const ChangePlanText = styled.div`
    font-family: "DM Sans", sans-serif;
    font-size: 16px;
    font-weight: 500;
    color: ${colors.PrimaryPurple500};
    cursor: pointer;
    text-align: end;
    &:focus-visible {
        outline: 2px solid #625B71;
        outline-offset: 2px;
    }
`

export const SecurePaymentText = styled.div`
    font-family: "DM Sans", sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: ${colors.textBlue};
    letter-spacing: 0%;
    text-align: center;
    /* Was a fixed 340px, which overflowed the card at 375/430. Cap at 340px on
       wide screens but let it shrink fluidly on narrow ones. */
    width: 100%;
    max-width: 340px;
`

export const StyledSubscriptionRowItem = styled.div`
  display: flex;
  border-radius: 2px;
  padding: 4px 8px 4px 8px;
  background-color: ${props => props.backgroundColor};
  color: ${props => props.textColor};
`;